/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, {
	forwardRef,
	type Ref,
	useCallback,
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
} from 'react';

import { jsx } from '@compiled/react';
import { bind } from 'bind-event-listener';

import { cssMap } from '@atlaskit/css';
import mergeRefs from '@atlaskit/ds-lib/merge-refs';
import noop from '@atlaskit/ds-lib/noop';
import { useNotifyOpenLayerObserver } from '@atlaskit/layering/experimental/open-layer-observer';
import { token } from '@atlaskit/tokens';

import { useAnimatedVisibility } from '../internal/use-animated-visibility';
import { useFocusWrap } from '../internal/use-focus-wrap';

import { type TDialogProps } from './types';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- dialog reset requires values not in cssMap's type union
const styles = cssMap({
	dialog: {
		// Reset browser defaults
		paddingBlockStart: token('space.0'),
		paddingInlineEnd: token('space.0'),
		paddingBlockEnd: token('space.0'),
		paddingInlineStart: token('space.0'),
		border: 'none',
		// @ts-expect-error -- cssMap types do not include 'none'
		maxWidth: 'none',
		// @ts-expect-error -- cssMap types do not include 'none'
		maxHeight: 'none',
		// Positioning
		margin: 'auto',
		// Override UA background: canvas. The dialog primitive is unopinionated;
		// consumers provide their own background on a child element.
		backgroundColor: 'transparent',
		// Backdrop
		'&::backdrop': {
			// @ts-expect-error -- cssMap types do not include blanket token
			backgroundColor: token('color.blanket'),
		},
	},
});

/**
 * Low-level `<dialog>` primitive. No visual opinions - no width, height,
 * background, border-radius, or layout. Consumers provide their own styling.
 *
 * Visibility is controlled declaratively via `isOpen`:
 * - `isOpen={true}` calls `.showModal()` (entry animation via `@starting-style`)
 * - `isOpen={false}` calls `.close()` (exit animation via `allow-discrete`)
 *
 * Handles native `cancel` event (Escape) and backdrop click detection.
 *
 * Close flow: we never call `dialog.close()` from event handlers. We always call
 * `onClose`; the consumer decides whether to set `isOpen={false}`.
 *
 * Accessibility: render at least one focusable element (typically a close
 * button) when `isOpen` becomes `true`. Tab is always trapped inside the
 * dialog, so keyboard users need somewhere to land.
 */
export const Dialog: React.ForwardRefExoticComponent<
	TDialogProps & React.RefAttributes<HTMLDialogElement>
> = forwardRef<HTMLDialogElement, TDialogProps>(function Dialog(
	{
		children,
		isOpen,
		onClose,
		onExitFinish,
		animate,
		style,
		testId,
		id: providedId,
		label,
		labelledBy,
		shouldHideBackdrop,
	}: TDialogProps,
	ref,
) {
	const generatedId = useId();
	const dialogId = providedId ?? generatedId;
	const ownRef = useRef<HTMLDialogElement>(null);
	const combinedRef = mergeRefs([ownRef, ref as Ref<HTMLDialogElement>]);

	// Animation lifecycle
	const { phase, preset } = useAnimatedVisibility({
		isOpen,
		animate,
		elementRef: ownRef,
		onExitFinish,
	});

	// Pre-computed phase predicate. `isVisible` means "host element is
	// mounted and on screen" (any phase except `closed`), used as a dep
	// signal for the backdrop-click rebind effect so it re-attaches to
	// the fresh <dialog> element after a close → reopen unmount/remount
	// cycle.
	const isVisible = phase !== 'closed';

	// Focus wrap
	// Native <dialog>.showModal() traps focus but wraps through <body> at
	// the boundary (A → B → C → body → A). This hook intercepts Tab to
	// wrap directly (A → B → C → A), matching the WAI-ARIA APG pattern.
	//
	// Takes `phase` directly: the listener stays attached while
	// `phase !== 'closed'`, i.e. through the animated-exit window.
	// Gating on `isOpen` would unbind the listener at the start of an
	// exit animation while the dialog is still visible, letting focus
	// escape (WCAG 2.4.3 Focus Order regression).
	useFocusWrap({ elementRef: ownRef, role: 'dialog', phase });

	// Open layer observer registration
	// Notifies the open layer observer so app-coordination features
	// (open-count subscriptions) work with top-layer dialogs.
	useNotifyOpenLayerObserver({
		type: 'modal',
		isOpen,
		// No-op: no current use case for programmatic close via OpenLayerObserver.
		onClose: noop,
	});

	// Show/hide the dialog in response to isOpen changes.
	useLayoutEffect(() => {
		const dialog = ownRef.current;
		if (!dialog) {
			return;
		}

		if (isOpen) {
			if (!dialog.open) {
				dialog.showModal();
			}
			return () => {
				if (dialog.open) {
					dialog.close();
				}
			};
		}

		if (dialog.open) {
			dialog.close();
		}
	}, [isOpen]);

	// Handle native Escape (cancel event)
	const handleCancel = useCallback(
		(event: React.SyntheticEvent<HTMLDialogElement>) => {
			event.preventDefault();
			onClose({ reason: 'escape' });
		},
		[onClose],
	);

	// Handle backdrop click
	// Attached via bind-event-listener rather than a React prop so we avoid
	// a11y lint suppressions on the <dialog> element.
	// Keyboard dismiss is already handled natively (Escape → onCancel above).
	// `isVisible` is in deps so the listener re-binds to the new <dialog>
	// element after a host unmount / remount cycle.
	useEffect(() => {
		const dialog = ownRef.current;
		if (!dialog) {
			return;
		}

		return bind(dialog, {
			type: 'click',
			listener(event) {
				if (event.target === event.currentTarget) {
					onClose({ reason: 'overlay-click' });
				}
			},
		});
	}, [onClose, isVisible]);

	// Atomic CSS (Compiled) deduplicates ::backdrop { background-color }
	// into a single class, making it impossible to toggle between two values
	// via cssMap entries. An ID-scoped <style> has higher specificity than
	// any atomic class, so the override always wins.
	const escapedId = CSS.escape(dialogId);

	// The `<dialog>` host element is only rendered while open or its exit
	// animation is playing. After exit completes the phase returns to
	// `closed` and we unmount the dialog entirely so it does not leave an
	// empty `role="dialog"` element in the accessibility tree. The element
	// re-mounts on the next `isOpen=true` commit; `showModal()` is called
	// from the existing `useLayoutEffect([isOpen])` on that same commit,
	// and listeners (cancel, click) re-bind via the `[onClose, isVisible]`
	// effect re-running after remount (React clears the ref on unmount, so
	// we need the listener effect to re-run with the fresh element).
	if (!isVisible) {
		return null;
	}

	return (
		<dialog
			ref={combinedRef}
			id={dialogId}
			// `aria-modal` is intentionally NOT set: native `<dialog>.showModal()`
			// already conveys modal semantics to assistive tech, and double-
			// declaring it forecloses non-modal use cases (consumers calling
			// `.show()` would still appear modal). Modern AT (NVDA / JAWS /
			// VoiceOver) infer modality from the platform accessibility API.
			aria-label={label}
			aria-labelledby={label ? undefined : labelledBy}
			css={styles.dialog}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
			style={style}
			onCancel={handleCancel}
			data-testid={testId}
			{...(preset ? { [`data-ds-dialog-${preset.name}`]: '' } : undefined)}
		>
			{/* Use an ID-scoped <style> to make the backdrop transparent because
			atomic CSS (Compiled) deduplicates the ::backdrop rule into a single
			shared class, so we cannot conditionally override it with cssMap.
			The ID selector has higher specificity and always wins. */}
			{shouldHideBackdrop && (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles
				<style>{`#${escapedId}::backdrop{background-color:transparent}`}</style>
			)}
			{children}
		</dialog>
	);
});
